import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Package, Plus, Trash2, DollarSign, Edit3, Check, X, Info } from 'lucide-react';
import { Toggle } from './Toggle';
import { useTakeawayPackages, TakeawayPackage } from './TakeawayPackagesContext';
export function TakeawayPackagesPanel() {
  const {
    packages,
    addPackage,
    updatePackage,
    deletePackage
  } = useTakeawayPackages();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<{
    name: string;
    description: string;
    price: string;
  }>({
    name: '',
    description: '',
    price: ''
  });
  const startEdit = (pkg: TakeawayPackage) => {
    setEditingId(pkg.id);
    setDraft({
      name: pkg.name,
      description: pkg.description,
      price: pkg.price.toString()
    });
  };
  const cancelEdit = () => {
    setEditingId(null);
    setDraft({
      name: '',
      description: '',
      price: ''
    });
  };
  const saveEdit = () => {
    if (!editingId) return;
    const priceNum = parseFloat(draft.price);
    if (!draft.name.trim() || isNaN(priceNum) || priceNum < 0) return;
    updatePackage(editingId, {
      name: draft.name.trim(),
      description: draft.description.trim(),
      price: priceNum
    });
    cancelEdit();
  };
  const handleAdd = () => {
    addPackage({
      name: 'New package',
      description: '',
      price: 0,
      active: true
    });
  };
  return <div className="flex flex-col gap-4 lg:col-span-2" data-id="element-3585">
      {/* Info banner */}
      <div className="bg-blue-50/60 border border-blue-100 rounded-xl p-4 flex gap-3" data-id="element-3586">
        <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0" data-id="element-3587">
          <Info size={16} data-id="element-3588" />
        </div>
        <div className="flex-1" data-id="element-3589">
          <div className="text-sm font-semibold text-blue-900" data-id="element-3590">
            How takeaway packaging works
          </div>
          <p className="text-xs text-blue-700/90 mt-1 leading-relaxed" data-id="element-3591">
            Define your packaging types here, then assign one to each menu item
            in the item editor. When a customer checks out for takeaway, the
            assigned package is added to their cart automatically — they never
            see this step. Use it to recover packaging costs without cluttering
            the customer experience.
          </p>
        </div>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-1" data-id="element-3592">
        <div data-id="element-3593">
          <h2 className="text-lg font-bold text-gray-900" data-id="element-3594">Takeaway Packages</h2>
          <p className="text-xs text-gray-500 mt-0.5" data-id="element-3595">
            {packages.length} package{packages.length === 1 ? '' : 's'}{' '}
            configured
          </p>
        </div>
        <button onClick={handleAdd} className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2.5 sm:py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 shadow-sm w-full sm:w-auto" data-id="element-3596">
          <Plus size={16} data-id="element-3597" />
          New Package
        </button>
      </div>

      {/* Package List */}
      <div className="flex flex-col gap-3" data-id="element-3598">
        <AnimatePresence mode="popLayout" data-id="element-3599">
          {packages.map(pkg => {
          const isEditing = editingId === pkg.id;
          return <motion.div key={pkg.id} layout initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.96
          }} transition={{
            duration: 0.18
          }} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-3600">
                {isEditing ? <div className="space-y-3" data-id="element-3601">
                    <input type="text" value={draft.name} onChange={e => setDraft({
                ...draft,
                name: e.target.value
              })} placeholder="Package name" className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm font-semibold focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition" data-id="element-3602" />
                    <textarea value={draft.description} onChange={e => setDraft({
                ...draft,
                description: e.target.value
              })} placeholder="What is this package used for?" rows={2} className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition resize-none" data-id="element-3603" />
                    <div className="flex items-center gap-3" data-id="element-3604">
                      <div className="relative w-32" data-id="element-3605">
                        <DollarSign size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-3606" />
                        <input type="number" step="0.01" min="0" value={draft.price} onChange={e => setDraft({
                    ...draft,
                    price: e.target.value
                  })} placeholder="0.00" className="w-full pl-8 pr-2 py-2 rounded-lg border border-gray-200 text-sm font-medium focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition" data-id="element-3607" />
                      </div>
                      <div className="flex-1 flex justify-end gap-2" data-id="element-3608">
                        <button onClick={cancelEdit} className="px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-100 transition-colors flex items-center gap-1" data-id="element-3609">
                          <X size={13} data-id="element-3610" /> Cancel
                        </button>
                        <button onClick={saveEdit} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-orange-500 text-white hover:bg-orange-600 transition-colors shadow-sm flex items-center gap-1" data-id="element-3611">
                          <Check size={13} data-id="element-3612" /> Save
                        </button>
                      </div>
                    </div>
                  </div> : <div className="flex items-start gap-4" data-id="element-3613">
                    <div className="w-11 h-11 rounded-xl bg-orange-50 text-orange-600 flex items-center justify-center flex-shrink-0" data-id="element-3614">
                      <Package size={20} data-id="element-3615" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-3616">
                      <div className="flex items-center gap-2 flex-wrap" data-id="element-3617">
                        <h3 className="font-bold text-gray-900 text-[15px]" data-id="element-3618">
                          {pkg.name}
                        </h3>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 text-[11px] font-bold tracking-wide" data-id="element-3619">
                          +${pkg.price.toFixed(2)}
                        </span>
                        {!pkg.active && <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-gray-100 text-gray-500 text-[11px] font-bold uppercase tracking-wide" data-id="element-3620">
                            Disabled
                          </span>}
                      </div>
                      {pkg.description && <p className="text-sm text-gray-500 mt-1.5 leading-relaxed" data-id="element-3621">
                          {pkg.description}
                        </p>}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0" data-id="element-3622">
                      <Toggle checked={pkg.active} onChange={v => updatePackage(pkg.id, {
                  active: v
                })} data-id="element-3623" />
                      <button onClick={() => startEdit(pkg)} className="text-gray-400 hover:text-orange-500 transition-colors p-1.5" aria-label="Edit package" data-id="element-3624">
                        <Edit3 size={16} data-id="element-3625" />
                      </button>
                      <button onClick={() => deletePackage(pkg.id)} className="text-gray-400 hover:text-red-500 transition-colors p-1.5" aria-label="Delete package" data-id="element-3626">
                        <Trash2 size={16} data-id="element-3627" />
                      </button>
                    </div>
                  </div>}
              </motion.div>;
        })}
        </AnimatePresence>

        {packages.length === 0 && <div className="bg-white rounded-xl border border-dashed border-gray-300 p-10 flex flex-col items-center justify-center text-center" data-id="element-3628">
            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3" data-id="element-3629">
              <Package size={22} className="text-gray-400" data-id="element-3630" />
            </div>
            <h3 className="font-semibold text-gray-900 text-sm mb-1" data-id="element-3631">
              No packages yet
            </h3>
            <p className="text-xs text-gray-500 max-w-xs" data-id="element-3632">
              Add your first takeaway package to start assigning packaging to
              menu items.
            </p>
            <button onClick={handleAdd} className="mt-4 px-4 py-2 rounded-lg text-xs font-semibold bg-orange-500 text-white hover:bg-orange-600 transition-colors shadow-sm flex items-center gap-1.5" data-id="element-3633">
              <Plus size={13} data-id="element-3634" />
              New Package
            </button>
          </div>}
      </div>
    </div>;
}